/**
 * How long the guide takes to read, per chapter and in total.
 *
 * Counted from the same content the page renders, so it cannot drift:
 * add a dashboard section and both the chapter and the total go up on
 * their own. Nobody has to remember to bump a number.
 *
 * ── The numbers behind it ─────────────────────────────────────────────
 * 200 words per minute. Blog estimators typically use 200–265 (Medium
 * uses 265); the low end is the honest choice here because this is
 * instructional prose with numbered steps and defined terms — read to be
 * followed, not skimmed.
 *
 * Screenshots cost time too. A reader stops, looks, and finds the thing
 * being described. Eight seconds each is a deliberate under-estimate: it
 * is enough to stop the figure being a lie, and small enough that it
 * never dominates. Medium's own model tapers from 12s down to 3s; a flat
 * rate is easier to reason about and lands in the same place for a
 * chapter with a handful of images.
 *
 * Rounded up, floor of one minute — "0 min read" helps nobody.
 */

import { DEPLETIONS_SECTIONS, type SectionGuide } from './depletionsSections';
import { GLOSSARY } from './guideGlossary';
import {
  OVERSIGHT_TOPICS,
  REGIONAL_SALES_ACTIONS,
  REGIONAL_SALES_TABS,
} from './regionalSalesGuide';
import {
  ACCESS_NOTES,
  ADMIN_CAPABILITIES,
  AUTH_CAUTION,
  AUTH_TOPICS,
  AREA_OVERVIEW,
  CHAPTER_INTROS,
  FAQ,
  FEEDBACK_INTRO,
  FEEDBACK_NOTES,
  GETTING_STARTED,
  INTRODUCTION,
  ROLES,
  SESSION_ENDINGS,
  SESSION_RATIONALE,
} from './userGuide';
import { BUCKET_META, BUCKET_ORDER } from '../lib/followUpBuckets';
import { OUTCOME_GUIDE, OUTCOME_ORDER } from '../lib/visitOutcomes';
import { PREMISES_STYLE } from '../components/premisesStyle';

const WORDS_PER_MINUTE = 200;
const SECONDS_PER_IMAGE = 8;

/** Words in a string, tolerant of the `**bold**` / `` `code` `` markup. */
function countWords(text: string): number {
  return text.trim().split(/\s+/).filter(Boolean).length;
}

function countAll(texts: (string | undefined)[]): number {
  return texts.reduce<number>((sum, t) => sum + (t ? countWords(t) : 0), 0);
}

/** Every word a topic contributes: its lead, its steps, its notes. */
function countGuide(guide: SectionGuide): number {
  return countAll([guide.what, ...guide.how, ...(guide.tips ?? []), guide.admin]);
}

interface ChapterCost {
  words: number;
  images: number;
}

/**
 * Per-chapter word and image counts, keyed by the chapter ids used in
 * `UserGuidePage`. Adding a chapter means adding an entry here — the one
 * bit of coupling, and it fails loudly (a missing chapter shows no
 * estimate rather than a wrong one).
 */
function chapterCosts(): Record<string, ChapterCost> {
  const depletionsWords =
    countAll([CHAPTER_INTROS.depletions]) +
    DEPLETIONS_SECTIONS.reduce((sum, s) => sum + countWords(s.title) + countGuide(s.guide), 0);

  const repTopics = [...REGIONAL_SALES_TABS, ...REGIONAL_SALES_ACTIONS];
  const regionalWords =
    countAll([CHAPTER_INTROS['regional-sales']]) +
    repTopics.reduce((sum, t) => sum + countWords(t.title) + countGuide(t.guide), 0);

  const oversightWords =
    countAll([CHAPTER_INTROS.oversight]) +
    OVERSIGHT_TOPICS.reduce((sum, t) => sum + countWords(t.title) + countGuide(t.guide), 0);

  // The glossary chapter opens with the colour keys, which are as much
  // reading as the definitions that follow.
  const keyWords =
    BUCKET_ORDER.reduce((sum, b) => {
      const meta = BUCKET_META[b];
      return sum + (meta ? countWords(meta.action) + countWords(meta.desc) : 0);
    }, 0) +
    OUTCOME_ORDER.reduce((sum, o) => sum + countWords(OUTCOME_GUIDE[o].when), 0) +
    Object.values(PREMISES_STYLE).reduce((sum, p) => sum + countWords(p.description), 0);

  return {
    introduction: {
      words:
        INTRODUCTION.reduce(
          (sum, p) => sum + countAll([p.heading, p.body, ...(p.bullets ?? [])]),
          0,
        ) + AREA_OVERVIEW.reduce((sum, a) => sum + countAll([a.name, a.summary, a.audience]), 0),
      images: 0,
    },
    'getting-started': {
      words: GETTING_STARTED.reduce((sum, s) => sum + countAll([s.title, s.body]), 0),
      images: 1,
    },
    depletions: { words: depletionsWords, images: 1 + DEPLETIONS_SECTIONS.length },
    'regional-sales': { words: regionalWords, images: 1 + repTopics.length },
    oversight: { words: oversightWords, images: 1 + OVERSIGHT_TOPICS.length },
    access: {
      words:
        AUTH_TOPICS.reduce((sum, t) => sum + countAll([t.heading, t.body]), 0) +
        SESSION_ENDINGS.reduce((sum, e) => sum + countAll([e.trigger, e.timing, e.body]), 0) +
        countAll([SESSION_RATIONALE, AUTH_CAUTION.title, AUTH_CAUTION.body]) +
        ROLES.reduce((sum, r) => sum + countAll([r.displayName, r.grants]), 0) +
        ACCESS_NOTES.reduce((sum, p) => sum + countAll([p.heading, p.body]), 0) +
        ADMIN_CAPABILITIES.reduce((sum, c) => sum + countAll([c.name, c.summary]), 0),
      images: 0,
    },
    glossary: {
      words: keyWords + GLOSSARY.reduce((sum, g) => sum + countAll([g.term, g.definition]), 0),
      images: 0,
    },
    faq: {
      words: FAQ.reduce((sum, f) => sum + countAll([f.question, f.answer]), 0),
      images: 0,
    },
    feedback: { words: countAll([FEEDBACK_INTRO, ...FEEDBACK_NOTES]), images: 0 },
  };
}

const COSTS = chapterCosts();

function toMinutes({ words, images }: ChapterCost): number {
  const seconds = (words / WORDS_PER_MINUTE) * 60 + images * SECONDS_PER_IMAGE;
  return Math.max(1, Math.ceil(seconds / 60));
}

/** Minutes for one chapter, or null if it has no measured content. */
export function chapterReadingMinutes(chapterId: string): number | null {
  const cost = COSTS[chapterId];
  return cost ? toMinutes(cost) : null;
}

/**
 * Minutes for the whole guide.
 *
 * Summed from the raw counts, not from the per-chapter minutes — adding
 * nine independently rounded-up numbers overstates the total by several
 * minutes.
 */
export function totalReadingMinutes(): number {
  const total = Object.values(COSTS).reduce<ChapterCost>(
    (sum, c) => ({ words: sum.words + c.words, images: sum.images + c.images }),
    { words: 0, images: 0 },
  );
  return toMinutes(total);
}
